import { useState } from "react"
import { Box, Typography, TextField } from "@mui/material"
import { useForm, Controller } from "react-hook-form"
import Modal from "../components/Modal"
import { getSlugFromURL } from "../utils"
import { FetchHelper } from "../utils/fetchHelper"
import { showError, showSuccess } from "../utils/snackbar"
import { useLocalization } from "../providers/LocalizationProvider"

const SaveAsTemplateModal = ({
    templateInjectionModal,
    setTemplateInjectionModal,
    tasksData = [], // 👈 required tasks array from parent
}) => {
    const {
        control,
        handleSubmit,
        reset,
        formState: { errors, isValid },
    } = useForm({
        mode: "onChange",
        defaultValues: {
            templateName: "",
            description: "",
        },
    })
    const [saveAsTemplateLoading, setSaveAsTemplateLoading] = useState(false)
    const { voiceiiTags } = useLocalization()

    // ✅ Close handler
    const handleClose = () => {
        setTemplateInjectionModal({ open: false })
        reset()
    }

    // ✅ Submit handler
    const onSubmit = async (data) => {
        if (!tasksData?.length) {
            alert(voiceiiTags("Tasks are required"))
            return
        }
        setSaveAsTemplateLoading(true)

        const payload = {
            template_name: data.templateName,
            description: data.description || "",
            tasks: tasksData,
            function_name: "save_as_template",
            slug: getSlugFromURL(),
            entity_id: "123abc",
        }

        try {
            const resp = await FetchHelper.post("api/v1/asset-detail/extension", payload)

            if (resp?.data?.status) {
                showSuccess(resp.data.message)
                handleClose()
            } else {
                showError(resp.data.message)
            }
            handleClose()
        } catch (error) {
            console.error("Save template failed:", error)
            showError(error.response?.data?.error?.message)
            setSaveAsTemplateLoading(false)
        } finally {
            setSaveAsTemplateLoading(false)
        }
    }

    return (
        <Modal
            status={templateInjectionModal.open}
            onClose={handleClose}
            onSave={handleSubmit(onSubmit)}
            maxWidth="sm"
            modalHeading={voiceiiTags("Save as Template")}
            confirmBtn
            disableSaveBtn={!isValid || !tasksData?.length} // ✅ disable if invalid
            isSubmitting={saveAsTemplateLoading}
        >
            <Box>
                {/* ===== Template Name ===== */}
                <Typography mb={1}>
                    {voiceiiTags("Template Name")} <span style={{ color: "red" }}>*</span>
                </Typography>

                <Controller
                    name="templateName"
                    control={control}
                    rules={{ required: voiceiiTags("Template name is required") }}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            fullWidth
                            placeholder="e.g., Standard Morning Care"
                            error={!!errors.templateName}
                            helperText={errors.templateName?.message}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "10px",
                                    "&.Mui-focused fieldset": {
                                        borderColor: "#00A76F",
                                    },
                                },
                            }}
                        />
                    )}
                />

                {/* ===== Description ===== */}
                <Typography mt={3} mb={1}>
                    {voiceiiTags("Description")}
                </Typography>

                <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            fullWidth
                            multiline
                            rows={3}
                            placeholder={voiceiiTags("Optional description...")}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "10px",
                                },
                            }}
                        />
                    )}
                />
            </Box>
        </Modal>
    )
}

export default SaveAsTemplateModal
